/**
 * The justificatifs of an arrêt: PDF files, listed with a way to add and remove them.
 *
 * Presentational only — the caller owns where the files live. On a declared arrêt they are
 * leave attachments already on the server (`url` set, removal calls the API); while a new
 * arrêt is being composed they are files held in memory until it is saved, since there is no
 * planning leave to attach them to yet.
 */
import React, { useRef } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast } from "../../app/utils/helpers";
import { confirmDialog } from "../popups/ConfirmDialog";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1000) return `${bytes} o`;
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} Ko`;
  return `${(bytes / (1000 * 1000)).toFixed(1)} Mo`;
};

/** Checked before sending; the server checks the file's own bytes again. */
const isPdf = (file) => file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));

/**
 * @param {Object} props
 * @param {Array<{key, name, size, url?}>} props.items - the files to list
 * @param {boolean} props.loading
 * @param {boolean} props.busy - an upload is in flight
 * @param {boolean} props.canEdit - offer add / remove
 * @param {Function} props.onAdd - called with the picked File
 * @param {Function} props.onRemove - called with the item
 * @param {string|null} [props.titleKey] - heading; null when a FormRow label already names it
 * @param {string|null} [props.helpKey] - small text under the list; null for none
 * @param {string} [props.addKey] - the add button's label
 */
export default function WorkStoppageAttachments({
  items, loading, busy, canEdit, onAdd, onRemove,
  titleKey = "WORK_STOPPAGE_JUSTIFICATIFS", helpKey = "WORK_STOPPAGE_JUSTIFICATIFS_HELP", addKey = "WORK_STOPPAGE_ATTACHMENT_ADD",
}) {
  const inputRef = useRef(null);

  const onPick = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!isPdf(file)) {
      showErrorToast(Localize("ATTACHMENT_ONLY_PDF"));
      return;
    }
    onAdd(file);
  };

  // A file already on the server is asked about first; one still held in memory is only
  // dropped from the pending list, so it goes straight away.
  const askRemove = (item) => {
    if (!item.url) {
      onRemove(item);
      return;
    }
    confirmDialog({
      title: <Langs str="DELETE_FILE" />,
      message: <Langs str="DELETE_FILE_MSG" />,
      children: <Typography sx={{ mt: 1, fontWeight: 600, wordBreak: "break-word" }}>{item.name}</Typography>,
      danger: true,
      onConfirm: () => onRemove(item),
    });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent={titleKey ? "space-between" : "flex-end"} sx={{ mb: 0.5 }}>
        {titleKey && (
          <Typography variant="overline" color="text.secondary">
            <Langs str={titleKey} />
          </Typography>
        )}
        {canEdit && (
          <>
            <Button
              size="small"
              startIcon={<AttachFileRoundedIcon />}
              onClick={() => inputRef.current && inputRef.current.click()}
              disabled={busy}
            >
              <Langs str={addKey} />
            </Button>
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden onChange={onPick} />
          </>
        )}
      </Stack>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "10px", overflow: "hidden" }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, opacity: 0.7 }}>
            <Langs str="LOADING" />
          </Typography>
        ) : null}
        {!loading && !(items || []).length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, opacity: 0.7 }}>
            <Langs str="WORK_STOPPAGE_NO_ATTACHMENTS" />
          </Typography>
        ) : null}
        {!loading && (items || []).map((item, index) => (
          <Stack
            key={item.key}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.5, py: 1, borderTop: index ? "1px solid" : "none", borderColor: "divider" }}
          >
            <PictureAsPdfRoundedIcon fontSize="small" sx={{ color: "#B91C1C", flexShrink: 0 }} />
            <Typography
              variant="body2"
              component={item.url ? "a" : "span"}
              href={item.url || undefined}
              target={item.url ? "_blank" : undefined}
              rel={item.url ? "noopener noreferrer" : undefined}
              title={item.name}
              sx={{
                flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: item.url ? "primary.main" : "text.primary",
                textDecoration: "none",
                "&:hover": item.url ? { textDecoration: "underline" } : {},
              }}
            >
              {item.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatSize(item.size)}
            </Typography>
            {canEdit && (
              <Tooltip title={Localize("Delete")}>
                <IconButton size="small" color="error" onClick={() => askRemove(item)} disabled={busy}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Box>
      {helpKey && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, opacity: 0.8 }}>
          <Langs str={helpKey} />
        </Typography>
      )}
    </Box>
  );
}
